/**
 * Subtabs — horizontal tabs at the foot of the Crown.
 * Mono counts tucked next to each label; active tab has an accent 2 px bottom
 * rule and the count pill flips to accent-soft + accent ink. Sits flush to
 * the crown's edges via negative margins (`-mx-8` cancels Crown's `px-8`,
 * `-mb-4` cancels Crown's `pb-4`).
 *
 * Source: .agents/design/project/artboard-radical.jsx:76-106
 */

import type { ReactNode } from 'react';

export interface SubtabItem<T extends string | number> {
  value: T;
  label: ReactNode;
  count?: number;
}

interface SubtabsProps<T extends string | number> {
  items: SubtabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Right-side overflow / hint slot (e.g. `<Kbd>⌘K</Kbd>`). */
  trailing?: ReactNode;
  'aria-label'?: string;
  className?: string;
}

export function Subtabs<T extends string | number>({
  items,
  value,
  onChange,
  trailing,
  className = '',
  ...rest
}: SubtabsProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={rest['aria-label']}
      className={
        // Below `lg` (14.2, widened after iPad-portrait testing): tabs are a
        // horizontally scrollable strip — no wrap, momentum scroll. The
        // tablet center column squeezed by the inspector is narrower than a
        // phone, so the scroll can't be phone-only. Negative margins cancel
        // the Crown's gutter at each breakpoint (px-8 ≥sm, px-4 phone).
        '-mx-8 -mb-4 mt-4 flex border-t border-line px-6 max-lg:overflow-x-auto max-lg:[&>button]:shrink-0 max-sm:-mx-4 max-sm:px-2 ' +
        className
      }
    >
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={String(item.value)}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.value)}
            className={[
              'relative -mb-px inline-flex cursor-pointer items-center gap-2 border-b-2 border-transparent px-3.5 pb-[11px] pt-3 text-[12.5px] transition-colors',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:-outline-offset-2',
              active ? 'font-semibold text-ink' : 'font-medium text-ink-3 hover:text-ink',
            ].join(' ')}
          >
            <span>{item.label}</span>
            {item.count != null && (
              <span
                className={
                  'px-1.5 py-px font-mono text-[10px] leading-[1.4] ' +
                  (active
                    ? 'bg-accent-soft text-accent-text'
                    : 'bg-bg text-ink-3')
                }
              >
                {item.count}
              </span>
            )}
            {active && (
              <span
                aria-hidden
                className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-accent"
                style={{ viewTransitionName: 'subtabs-active-indicator' }}
              />
            )}
          </button>
        );
      })}
      {trailing && (
        <div className="ml-auto inline-flex items-center gap-1.5 px-1.5 font-mono text-[10px] uppercase tracking-[0.06em] text-ink-3">
          {trailing}
        </div>
      )}
    </div>
  );
}
